import { Component, OnInit } from '@angular/core';
import { RdvparentalService, AppointmentModel, ApiResponse } from './rdvparental.service';
import { ChildrenSelectorComponent, Student } from '../../shared/children-selector/children-selector.component';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PageTitleComponent } from '../../shared/page-title/page-title.component';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-rdvparental',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    PageTitleComponent,
    ChildrenSelectorComponent,
    MatIconModule
  ],
  templateUrl: './rdvparental.component.html',
  styleUrls: ['./rdvparental.component.css']
})
export class RdvparentalComponent implements OnInit {
  selectedStudent: Student | null = null;
  availableAppointments: AppointmentModel[] = [];
  confirmedReservations: AppointmentModel[] = [];
  notConfirmedReservations: AppointmentModel[] = [];
  loading = false;
  message: string | null = null;
  messageType: string | null = null;
  // Indicates whether new slots have appeared since last fetch
  hasNewSlots = false;
  private previousAvailableCount = 0;
  private isReservationsLoaded = false;

  // Pagination properties
  itemsPerPage = 3;
  availableCurrentPage = 1;
  confirmedCurrentPage = 1;
  pendingCurrentPage = 1;

  get availableTotalPages(): number {
    return Math.ceil(this.availableAppointments.length / this.itemsPerPage);
  }

  get confirmedTotalPages(): number {
    return Math.ceil(this.confirmedReservations.length / this.itemsPerPage);
  }

  get pendingTotalPages(): number {
    return Math.ceil(this.notConfirmedReservations.length / this.itemsPerPage);
  }

  previousPage(type: string) {
    if (type === 'available' && this.availableCurrentPage > 1) {
      this.availableCurrentPage--;
    } else if (type === 'confirmed' && this.confirmedCurrentPage > 1) {
      this.confirmedCurrentPage--;
    } else if (type === 'pending' && this.pendingCurrentPage > 1) {
      this.pendingCurrentPage--;
    }
  }

  nextPage(type: string) {
    if (type === 'available' && this.availableCurrentPage < this.availableTotalPages) {
      this.availableCurrentPage++;
    } else if (type === 'confirmed' && this.confirmedCurrentPage < this.confirmedTotalPages) {
      this.confirmedCurrentPage++;
    } else if (type === 'pending' && this.pendingCurrentPage < this.pendingTotalPages) {
      this.pendingCurrentPage++;
    }
  }

  goToPage(page: number, type: string) {
    if (type === 'available' && page >= 1 && page <= this.availableTotalPages) {
      this.availableCurrentPage = page;
    } else if (type === 'confirmed' && page >= 1 && page <= this.confirmedTotalPages) {
      this.confirmedCurrentPage = page;
    } else if (type === 'pending' && page >= 1 && page <= this.pendingTotalPages) {
      this.pendingCurrentPage = page;
    }
  }

  getVisiblePages(type: string): (number | string)[] {
    const totalPages = type === 'available' ? this.availableTotalPages :
                       type === 'confirmed' ? this.confirmedTotalPages :
                       this.pendingTotalPages;
    const currentPage = type === 'available' ? this.availableCurrentPage :
                        type === 'confirmed' ? this.confirmedCurrentPage :
                        this.pendingCurrentPage;

    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  }

  private filterAvailable() {
    this.availableAppointments = this.availableAppointments.filter(slot =>
      !this.confirmedReservations.some(res => res.id == slot.id) &&
      !this.notConfirmedReservations.some(res => res.id == slot.id)
    );
  }

  isAlreadyReserved(slot: AppointmentModel): boolean {
    return this.confirmedReservations.some(res => res.id == slot.id) ||
           this.notConfirmedReservations.some(res => res.id == slot.id);
  }

  constructor(private rdvService: RdvparentalService, private translate: TranslateService) {}

  // Convert minutes (e.g., 780 -> 13h) or common time strings to a human-readable 'Hh' or 'HhMM' format
  formatAppointmentHour(value: string | number | null | undefined): string {
    if (value === null || value === undefined || String(value).trim() === '') return '-';
    const s = String(value).trim();

    // If already contains 'h' or ':' assume it's already a time-like string
    if (/\d+h\d*/i.test(s)) {
      // normalize '13h30' or '13h' -> ensure 'h' separator and minutes trimmed
      const parts = s.split(/h/i);
      const hh = parseInt(parts[0], 10);
      const mm = parts[1] ? parts[1].padStart(2, '0') : '';
      return mm && mm !== '00' ? `${hh}h${mm}` : `${hh}h`;
    }

    const colonMatch = s.match(/^(\d{1,2}):(\d{2})$/);
    if (colonMatch) {
      const hh = parseInt(colonMatch[1], 10);
      const mm = colonMatch[2];
      return mm === '00' ? `${hh}h` : `${hh}h${mm}`;
    }

    // If numeric string, interpret as minutes (e.g. 780 -> 13:00)
    if (/^\d+$/.test(s)) {
      const minutes = parseInt(s, 10);
      const hh = Math.floor(minutes / 60);
      const mm = minutes % 60;
      return mm === 0 ? `${hh}h` : `${hh}h${mm.toString().padStart(2, '0')}`;
    }

    // Fallback: return original string
    return s;
  }
  ngOnInit() {}

  onStudentSelected(student: Student) {
    this.selectedStudent = student;
    this.availableAppointments = [];
    this.confirmedReservations = [];
    this.notConfirmedReservations = [];
    this.isReservationsLoaded = false;
    this.fetchAllData();
  }

  fetchAllData() {
    if (!this.selectedStudent) return;
    const batchId = this.selectedStudent.batchId;
    const studentId = this.selectedStudent.idStudent;
    const guardianId = this.selectedStudent.idGuardian;
    this.loading = true;
    this.rdvService.getAvailableAppointments(batchId, studentId, guardianId).subscribe({
      next: (res) => {
        const prev = this.availableAppointments.length;
        this.availableAppointments = res.response || [];
        // Mark as new if count increased
        this.hasNewSlots = this.availableAppointments.length > prev;
        this.previousAvailableCount = this.availableAppointments.length;
        this.availableCurrentPage = 1; // Reset page
        if (this.isReservationsLoaded) {
          this.filterAvailable();
        }
      },
      error: () => {
        this.availableAppointments = [];
        this.availableCurrentPage = 1;
        if (this.isReservationsLoaded) {
          this.filterAvailable();
        }
        this.loading = false; // Ensure loading stops
      }
    });
    this.rdvService.listReservations(batchId, studentId, guardianId).subscribe({
      next: (res) => {
        this.confirmedReservations = res.response?.listResrvationConfirmed || [];
        this.notConfirmedReservations = res.response?.listResrvationNotConfirmed || [];
        this.isReservationsLoaded = true;
        this.filterAvailable();
        this.loading = false;
        this.confirmedCurrentPage = 1; // Reset page
        this.pendingCurrentPage = 1; // Reset page
        this.availableCurrentPage = 1; // Reset page after filter
      },
      error: () => {
        this.confirmedReservations = [];
        this.notConfirmedReservations = [];
        this.isReservationsLoaded = true;
        this.filterAvailable();
        this.loading = false;
        this.confirmedCurrentPage = 1;
        this.pendingCurrentPage = 1;
        this.availableCurrentPage = 1; // Reset page after filter
      }
    });
  }

  reserveAppointment(appointment: AppointmentModel) {
    if (!this.selectedStudent) return;
    // Check if already reserved
    const alreadyReserved = this.confirmedReservations.some(res => res.id === appointment.id) ||
                            this.notConfirmedReservations.some(res => res.id === appointment.id);
    if (alreadyReserved) {
      this.message = this.translate.instant('rdvparental.alreadyReserved');
      this.messageType = 'warning';
      return;
    }
    const batchId = this.selectedStudent.batchId;
    const studentId = this.selectedStudent.idStudent;
    const guardianId = this.selectedStudent.idGuardian;
    this.loading = true;
    this.rdvService.createReservation(batchId, studentId, guardianId, appointment.id).subscribe({
      next: (res) => {
        this.message = this.translate.instant('rdvparental.reservationSuccess');
        this.messageType = 'success';
        setTimeout(() => {
          if (this.messageType === 'success') {
            this.message = null;
            this.messageType = null;
          }
        }, 2000);
        this.fetchAllData();
      },
      error: () => {
        this.message = this.translate.instant('rdvparental.reservationError');
        this.messageType = 'error';
        this.loading = false;
      }
    });
  }
}
